import { COMPANION_ACTIVITIES } from "../types.js";

/** Tool definitions (SPEC 6.4), provider-neutral JSON Schema. */
export interface ToolDef {
  name: string;
  description: string;
  input_schema: {
    type: "object";
    properties: Record<string, unknown>;
    required: string[];
    additionalProperties: false;
  };
}

const AREA = {
  type: "string",
  enum: ["body", "mind", "work", "social", "home"],
  description: "body=Ciało, mind=Głowa, work=Praca, social=Relacje (ludzie), home=Dom (obowiązki)",
};
const SIZE = {
  type: "string",
  enum: ["S", "M", "L"],
  description: "S do 15 min, M 15–60 min, L ponad godzinę albo coś trudnego",
};
const ID = { type: "string", description: "id z listy w STANIE GRY" };
const DATE = { type: "string", description: "YYYY-MM-DD" };

const obj = (properties: Record<string, unknown>, required: string[] = []): ToolDef["input_schema"] => ({
  type: "object",
  properties,
  required,
  additionalProperties: false,
});

export const TOOLS: readonly ToolDef[] = [
  {
    name: "log_activity",
    description: "Zapisz coś, co gracz JUŻ zrobił, a czego nie ma na liście otwartych questów.",
    input_schema: obj(
      {
        area: AREA,
        text: { type: "string", description: "2–6 słów po polsku" },
        size: SIZE,
        animation: {
          type: "string",
          enum: [
            "train",
            "read",
            "work",
            "write",
            "trash",
            "clean",
            "cook",
            "shop",
            "coffee",
            "beer",
            "walk",
            "other",
          ],
          description: "animacja postaci pasująca do aktywności",
        },
      },
      ["area", "text", "size", "animation"],
    ),
  },
  {
    name: "add_quest",
    description:
      "Dodaj zadanie na przyszłość (plan, 'muszę', 'jutro chcę'). Stałe obowiązki dostają repeat_days.",
    input_schema: obj(
      {
        title: { type: "string", description: "krótko, po polsku" },
        area: AREA,
        size: SIZE,
        due: { type: "string", description: "today, tomorrow albo YYYY-MM-DD" },
        repeat_days: { type: "integer", minimum: 1, maximum: 60 },
      },
      ["title", "area", "size", "due"],
    ),
  },
  {
    name: "complete_quest",
    description: "Gracz zrobił quest z listy otwartych (dopasuj po sensie).",
    input_schema: obj({ id: ID }, ["id"]),
  },
  {
    name: "postpone_quest",
    description: "Przesuń otwarty quest (domyślnie na jutro).",
    input_schema: obj({ id: ID, to: DATE }, ["id"]),
  },
  {
    name: "drop_quest",
    description: "Gracz rezygnuje z questu albo jest nieaktualny.",
    input_schema: obj({ id: ID }, ["id"]),
  },
  {
    name: "add_reminder",
    description:
      "Przypomnienie, o które gracz prosi. at: 'HH:MM' (dziś, a jeśli już minęło to jutro), 'YYYY-MM-DDTHH:MM', 'wieczorem', 'rano', 'po południu' albo pusty (najbliższy wieczór).",
    input_schema: obj({ text: { type: "string" }, at: { type: "string" } }, ["text", "at"]),
  },
  {
    name: "set_mode",
    description:
      "Tryb gry: sick (choroba, pełna pauza), vacation (urlop, luz), crunch (zawał w robocie), normal (powrót).",
    input_schema: obj(
      { mode: { type: "string", enum: ["normal", "sick", "vacation", "crunch"] }, until: DATE },
      ["mode"],
    ),
  },
  {
    name: "mute",
    description:
      "Wyciszenie zaczepek. short: 'zamknij się', 'cicho' (3 h). today: 'daj mi spokój', 'nie teraz' (do końca dnia). days: 'wyłącz się na kilka dni', 'nie gadaj do weekendu' (until = pierwszy dzień, kiedy znowu wolno gadać; domyślnie za 3 dni).",
    input_schema: obj({ kind: { type: "string", enum: ["short", "today", "days"] }, until: DATE }, ["kind"]),
  },
  { name: "unmute", description: "Koniec wyciszenia ('wróć', 'możesz już gadać').", input_schema: obj({}) },
  {
    name: "money_event",
    description:
      "Prawdziwe pieniądze (premia, dodatkowy zarobek, podwyżka, wygrana). NIGDY nie zapisuj kwoty. small = drobne, medium = zauważalne, large = duże (np. premia, kilka tysięcy).",
    input_schema: obj(
      {
        tier: { type: "string", enum: ["small", "medium", "large"] },
        note: { type: "string", description: "bez kwot" },
      },
      ["tier"],
    ),
  },
  {
    name: "buy_item",
    description: "Kup przedmiot z katalogu za złoto avatara, tylko gdy go na to stać.",
    input_schema: obj({ item_id: { type: "string" } }, ["item_id"]),
  },
  {
    name: "remember_fact",
    description: "Zapamiętaj trwały fakt o graczu.",
    input_schema: obj({ fact: { type: "string" } }, ["fact"]),
  },
  {
    name: "forget_fact",
    description: "Zapomnij fakt (id z listy faktów).",
    input_schema: obj({ id: ID }, ["id"]),
  },
  {
    name: "shopping_add",
    description: "Dopisz pozycje do listy zakupów na lodówce.",
    input_schema: obj({ items: { type: "array", items: { type: "string" }, minItems: 1 } }, ["items"]),
  },
  {
    name: "shopping_remove",
    description: "Usuń pozycje z listy zakupów (kupione albo niepotrzebne).",
    input_schema: obj({ items: { type: "array", items: { type: "string" }, minItems: 1 } }, ["items"]),
  },
  { name: "shopping_clear", description: "Wyczyść całą listę zakupów.", input_schema: obj({}) },
  {
    name: "companion_do",
    description:
      "Ty (postać na ekranie) naprawdę robisz to, co właśnie mówisz o sobie: 'też się kładę' = sleep, 'idę na spacer' = walk_out, 'skoczę po zakupy' = shopping_out, 'to ja poczytam' = read. Tylko twoje czynności, nigdy gracza.",
    input_schema: obj(
      {
        activity: {
          type: "string",
          enum: [...COMPANION_ACTIVITIES],
          description: "walk_out i shopping_out: wychodzisz z mieszkania drzwiami i wracasz po czasie",
        },
        minutes: { type: "integer", description: "ile minut, opcjonalnie (domyślnie rozsądnie)" },
      },
      ["activity"],
    ),
  },
  {
    name: "rate_week",
    description: "Niedzielna ocena 'czujesz progres?' w skali 1–5.",
    input_schema: obj({ value: { type: "integer", minimum: 1, maximum: 5 } }, ["value"]),
  },
];

export const TOOL_NAMES: ReadonlySet<string> = new Set(TOOLS.map((t) => t.name));
